import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import logger from 'src/util/logger';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_community.admin_community_list.pagination_error.message',
    defaultMessage: 'Failed to load more communities. Please try again.',
  },
});

const AdminCommunityListFragment = graphql`
  fragment AdminCommunityList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminCommunityListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    id
    communities(
      first: $count
      after: $cursor
    ) @connection(key: "AdminCommunityList_communities") {
      edges {
        cursor
        node {
          uuid
          title
          externalIdentifier
          active { start finish }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows rowCount={3} />;
}

/**
 * Admin list of communities with load-more pagination. Field errors throw to the
 * route's PlaceholderErrorBoundary; load-more failures show an inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactComponentElement} AdminCommunityList component.
 */
function AdminCommunityList({ viewer: viewerRef = null }) {
  const {
    data: viewer, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminCommunityListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  const intl = useIntl();

  if (!viewer) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminCommunityList: the route query returned no viewer');
  }

  const { communities } = viewer;

  // Page and header text
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.page.title',
    defaultMessage: 'Enosi Admin - Communities',
  });
  const headerText = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.header.text',
    defaultMessage: 'Communities',
  });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbCommunities = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.breadcrumbs.communities.label',
    defaultMessage: 'Communities',
  });

  // Table headers
  const headerStatus = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.status.label',
    defaultMessage: 'Status',
  });
  const headerTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.title.label',
    defaultMessage: 'Title',
  });
  const headerId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.id.label',
    defaultMessage: 'ID',
  });
  const headerExtIdTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.external_identifier.abbr.title',
    defaultMessage: 'External identifier',
  });
  const headerExtIdLabel = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const headerPropertyCount = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.property_count.label',
    defaultMessage: 'Property Count',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more communities to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbCommunities },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{headerText}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{headerStatus}</th>
                <th scope="col">{headerTitle}</th>
                <th scope="col">{headerId}</th>
                <th scope="col">
                  <abbr title={headerExtIdTitle}>{headerExtIdLabel}</abbr>
                </th>
                <th scope="col">{headerPropertyCount}</th>
              </tr>
            </thead>
            <tbody>
              {
                // properties && <pre>{JSON.stringify(properties, null, 2)}</pre>
                communities && communities.edges && communities.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, title, externalIdentifier, active,
                  } = node;

                  return (
                    <tr key={`admin-community-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row">
                        <Link to={`/admin/communities/${uuid}`}>
                          <div>{title}</div>
                        </Link>
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={uuid} />
                        <br />
                        {`(${externalIdentifier})`}
                      </td>
                      <td aria-label="property-count" />
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminCommunityList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminCommunityList;
